import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ═══════════════════════════════════════════════════════════
   STORE CART SLICE
   Namespaced ("storeCart") twin of the marketplace cart, used
   by StoreProductsHome / StoreHome / checkout. Kept structurally
   identical to the original cart so the same UI actions work,
   plus a few extras the store flow actually needs:
     • per-brand cart guard helpers (single-brand checkout)
     • setCartQty for typed quantity inputs
     • hydrate/replace for restoring a saved cart
   Selectors at the bottom centralize subtotal / count math so
   components (and storeProductServiceFee) never re-derive it
   inconsistently.
═══════════════════════════════════════════════════════════ */

/* ================= TYPES ================= */

// Extra items (toppings, add-ons, gift wrap, etc)
export type StoreCartExtra = {
  id: string;
  name: string;
  price: number;
  qty?: number;
};

// What is STORED in Redux
export type StoreCartItem = {
  listingId: string;
  brandId: string; // 🔥 REQUIRED for delivery + single-brand checkout
  hostId: string;
  name: string;
  rate: number;
  photo?: string;
  qty: number;
  colorId?: number;
  sizeId?: number;
};

// What UI SENDS to addToCart
export type AddToStoreCartPayload = {
  listingId: string;
  brandId: string; // 🔥 REQUIRED
  hostId: string;
  name: string;
  rate: number;
  photo?: string;
  qty?: number;
  colorId?: number;
  sizeId?: number;
};

export type StoreVariantKey = {
  listingId: string;
  colorId?: number;
  sizeId?: number;
  variantId?: string;
};

export type StoreCartState = {
  userId: string | null;
  items: StoreCartItem[];
  extras: StoreCartExtra[];
};

/* ================= STATE ================= */

const initialState: StoreCartState = {
  userId: null,
  items: [],
  extras: [],
};

/* ================= HELPERS ================= */

const sameVariant = (i: StoreCartItem | StoreVariantKey, key: StoreVariantKey) =>
  i.listingId === key.listingId &&
  i.colorId === key.colorId &&
  i.sizeId === key.sizeId;

/* ================= SLICE ================= */

const storeCartSlice = createSlice({
  name: "storeCart",
  initialState,
  reducers: {
    setCartUser: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },

    /* ===== ADD TO CART ===== */
    addToCart: (state, action: PayloadAction<AddToStoreCartPayload>) => {
      const qtyToAdd = action.payload.qty ?? 1;
      const existing = state.items.find((i) =>
        sameVariant(i, action.payload)
      );
      if (existing) {
        existing.qty += qtyToAdd;
        // keep latest metadata (photo/name/rate can change between opens)
        existing.name = action.payload.name;
        existing.rate = action.payload.rate;
        existing.photo = action.payload.photo ?? existing.photo;
      } else {
        state.items.push({ ...action.payload, qty: qtyToAdd });
      }
    },

    /* ===== SET EXACT QTY (typed inputs) ===== */
    setCartQty: (
      state,
      action: PayloadAction<StoreVariantKey & { qty: number }>
    ) => {
      const item = state.items.find((i) => sameVariant(i, action.payload));
      if (!item) return;
      if (action.payload.qty <= 0) {
        state.items = state.items.filter((i) => !sameVariant(i, action.payload));
      } else {
        item.qty = action.payload.qty;
      }
    },

    /* ===== ADD EXTRA ===== */
    addExtra: (state, action: PayloadAction<StoreCartExtra>) => {
      const existing = state.extras.find((e) => e.id === action.payload.id);
      if (existing) {
        existing.qty = (existing.qty || 1) + (action.payload.qty || 1);
      } else {
        state.extras.push({ ...action.payload, qty: action.payload.qty || 1 });
      }
    },

    /* ===== REMOVE EXTRA ===== */
    removeExtra: (state, action: PayloadAction<string>) => {
      state.extras = state.extras.filter((e) => e.id !== action.payload);
    },

    /* ===== DECREMENT ===== */
    decrementCartItem: (state, action: PayloadAction<StoreVariantKey>) => {
      const item = state.items.find((i) => sameVariant(i, action.payload));
      if (!item) return;
      if (item.qty > 1) {
        item.qty -= 1;
      } else {
        state.items = state.items.filter((i) => !sameVariant(i, action.payload));
      }
    },

    /* ===== REMOVE ===== */
    removeFromCart: (state, action: PayloadAction<StoreVariantKey>) => {
      state.items = state.items.filter((i) => !sameVariant(i, action.payload));
    },

    /* ===== HYDRATE (restore a saved cart) ===== */
    replaceCart: (state, action: PayloadAction<StoreCartItem[]>) => {
      state.items = action.payload;
    },

    clearCart: (state) => {
      state.items = [];
      state.extras = [];
    },
  },
});

export const {
  setCartUser,
  addToCart,
  setCartQty,
  addExtra,
  removeExtra,
  decrementCartItem,
  removeFromCart,
  replaceCart,
  clearCart,
} = storeCartSlice.actions;

/* ================= SELECTORS ================= */
/* Import these instead of re-computing totals in components so the
   number a shopper sees always matches what storeProductServiceFee
   charges. `any` root keeps this drop-in regardless of how the
   store is typed in your RootState. */

export const selectStoreCartItems = (s: any): StoreCartItem[] =>
  s.storeCart?.items ?? [];

export const selectStoreCartExtras = (s: any): StoreCartExtra[] =>
  s.storeCart?.extras ?? [];

export const selectStoreCartCount = (s: any): number =>
  (s.storeCart?.items ?? []).reduce(
    (n: number, i: StoreCartItem) => n + i.qty,
    0
  );

export const selectStoreCartSubtotal = (s: any): number => {
  const items = s.storeCart?.items ?? [];
  const extras = s.storeCart?.extras ?? [];
  const itemsTotal = items.reduce(
    (sum: number, i: StoreCartItem) => sum + i.rate * i.qty,
    0
  );
  const extrasTotal = extras.reduce(
    (sum: number, e: StoreCartExtra) => sum + e.price * (e.qty || 1),
    0
  );
  return parseFloat((itemsTotal + extrasTotal).toFixed(2));
};

/* All distinct brandIds currently in the cart — handy for enforcing
   single-brand checkout and for grouping the cart by store. */
export const selectStoreCartBrandIds = (s: any): string[] =>
  Array.from(
    new Set((s.storeCart?.items ?? []).map((i: StoreCartItem) => i.brandId))
  );

export default storeCartSlice.reducer;
